import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ApiKeyPanel } from "./pub_integrations/ApiKeyPanel";
import { ApiChangelogBanner } from "./pub_integrations/ApiChangelog";
import { ReportingApiDocs } from "./pub_integrations/ReportingApiDocs";
import { PostbackUrlTab } from "./pub_integrations/PostbackUrlTab";
import { PubPostbackLogsTab } from "./pub_integrations/PubPostbackLogsTab";
import { TrackingLinksTab } from "./pub_integrations/TrackingLinksTab";
import { TryItPanel, TRY_ENDPOINTS } from "./pub_integrations/TryItPanel";

/**
 * Publisher → API & Integrations
 *
 * Orchestrator. Owns API-key + postback-URL state, the "Try it" playground
 * lifecycle (open/close/restore from localStorage), and renders the three
 * tab bodies as their own focused components under `./pub_integrations/`.
 */
export default function PubIntegrations() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState("");
  const [subId, setSubId] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [postbackUrl, setPostbackUrl] = useState("");
  const [savingPb, setSavingPb] = useState(false);
  const [macros, setMacros] = useState([]);
  const [loading, setLoading] = useState(true);
  const didFetch = useRef(false);

  // ─── "Try it" playground state ───
  // Persists in localStorage so a hard reload returns the publisher to
  // exactly the endpoint + params they were inspecting.
  const [tryEndpointKey, setTryEndpointKey] = useState(null);
  const [tryRestoredValues, setTryRestoredValues] = useState(null);
  const [tryAutoFireNonce, setTryAutoFireNonce] = useState(0);
  const tryEndpoint = tryEndpointKey ? TRY_ENDPOINTS[tryEndpointKey] : null;

  useEffect(() => {
    try {
      const raw = localStorage.getItem("redclub:api-playground:v1");
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && saved.endpointKey && TRY_ENDPOINTS[saved.endpointKey]) {
        setTryEndpointKey(saved.endpointKey);
        setTryRestoredValues(saved.values || {});
        setTryAutoFireNonce(Date.now());
      }
    } catch {}
  }, []);

  const openTry = (key) => {
    setTryRestoredValues(null);
    setTryAutoFireNonce(0);
    setTryEndpointKey(key);
  };
  const closeTry = () => {
    setTryEndpointKey(null);
    try { localStorage.removeItem("redclub:api-playground:v1"); } catch {}
  };

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;
    (async () => {
      try {
        const [keyResp, pbResp] = await Promise.all([
          API.get("/publisher/api-key"),
          API.get("/publisher/postback-url"),
        ]);
        setApiKey(keyResp.data.api_key || "");
        setSubId(keyResp.data.sub_id || "");
        setPostbackUrl(pbResp.data.postback_url || "");
        setMacros(pbResp.data.available_macros || []);
      } catch (err) {
        console.error("[integrations] failed to load api-key + postback:", err);
      }
      setLoading(false);
    })();
  }, []);

  const generateKey = async () => {
    setGenerating(true);
    try {
      const { data } = await API.post("/publisher/api-key/generate");
      setApiKey(data.api_key);
      setShowKey(true);
      toast.success("API key generated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const revokeKey = async () => {
    if (!window.confirm("Revoke this API key? Any existing integrations using it will stop working.")) return;
    try {
      await API.delete("/publisher/api-key");
      setApiKey("");
      toast.success("API key revoked");
    } catch {
      toast.error("Failed");
    }
  };

  const savePostback = async () => {
    setSavingPb(true);
    try {
      await API.post("/publisher/postback-url", { postback_url: postbackUrl });
      toast.success("Postback URL saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPb(false);
    }
  };

  const copyText = (text) => navigator.clipboard.writeText(text);

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>;

  const productsEnabled = user?.products_enabled || [];

  return (
    <div
      className={`p-8 transition-[padding] duration-200 ${tryEndpoint ? "sm:pr-[460px]" : ""}`}
      data-testid="publisher-integrations-page"
    >
      <TryItPanel
        endpointKey={tryEndpointKey}
        endpoint={tryEndpoint}
        apiKey={apiKey}
        restoredValues={tryRestoredValues}
        autoFireNonce={tryAutoFireNonce}
        onClose={closeTry}
      />

      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-400 mb-2">
          Developer / Connections
        </p>
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">API & Integrations</h1>
        <p className="text-sm text-slate-500 mt-2">Connect your systems with our reporting API and postback tracking</p>
      </div>

      <Tabs defaultValue="api" className="w-full max-w-4xl">
        <TabsList className="border-b border-slate-200 bg-transparent h-auto p-0 mb-6 rounded-none">
          <TabsTrigger value="api" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="tab-api">
            Reporting API
          </TabsTrigger>
          <TabsTrigger value="postback" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="tab-postback">
            Postback URL
          </TabsTrigger>
          <TabsTrigger value="postback-logs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="tab-postback-logs">
            Postback Logs
          </TabsTrigger>
          <TabsTrigger value="tracking" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:bg-transparent data-[state=active]:text-[#002FA7] px-5 py-2.5 text-sm font-medium" data-testid="tab-tracking">
            Tracking Links Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="api" className="space-y-6">
          <ApiChangelogBanner />
          <ApiKeyPanel
            apiKey={apiKey}
            showKey={showKey}
            generating={generating}
            onToggleShow={() => setShowKey(!showKey)}
            onCopy={copyText}
            onGenerate={generateKey}
            onRevoke={revokeKey}
          />
          <ReportingApiDocs
            apiKey={apiKey}
            subId={subId}
            productsEnabled={productsEnabled}
            onTry={openTry}
          />
        </TabsContent>

        <TabsContent value="postback">
          <PostbackUrlTab
            postbackUrl={postbackUrl}
            setPostbackUrl={setPostbackUrl}
            savingPb={savingPb}
            macros={macros}
            subId={subId}
            onSave={savePostback}
          />
        </TabsContent>

        <TabsContent value="postback-logs">
          <PubPostbackLogsTab />
        </TabsContent>

        <TabsContent value="tracking">
          <TrackingLinksTab subId={subId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
